import type { AdminMeResponse } from '@diana/contracts';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AdminApiError } from '../lib/api-client.js';
import { useAdminApi } from './AdminApiContext.js';
import { SessionGate, type SessionPhase } from '../features/session/SessionGate.js';

type Me = ReturnType<typeof AdminMeResponse.parse>;
const SessionActions = createContext({ logout: () => {} });
export const useAdminSessionActions = () => useContext(SessionActions);

export function AdminSessionBoundary({ children }: { children(me: Me): ReactNode }) {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState<Me | null>(null);
  const currentRef = useRef(current);
  currentRef.current = current;
  const [phase, setPhase] = useState<SessionPhase>('checking');
  const phaseRef = useRef(phase);
  const epoch = useRef(0);
  const mounted = useRef(false);
  const logoutBusy = useRef(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  const enter = useCallback(
    (next: SessionPhase) => {
      if (phaseRef.current === 'ready' && next !== 'ready')
        returnFocus.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
      phaseRef.current = next;
      api.setSessionLocked(next !== 'ready');
      setPhase(next);
    },
    [api],
  );
  const accept = useCallback(
    (me: Me) => {
      const previous = currentRef.current;
      if (previous && (previous.user.id !== me.user.id || previous.user.role !== me.user.role)) {
        api.adoptSession(me);
        queryClient.clear();
      }
      currentRef.current = me;
      queryClient.setQueryData(['me'], me);
      setCurrent(me);
      enter('ready');
      if (previous)
        void queryClient
          .invalidateQueries({
            predicate: (query) => query.queryKey[0] !== 'me',
            refetchType: 'active',
          })
          .catch(() => undefined);
    },
    [api, queryClient, enter],
  );
  const check = useCallback(async () => {
    if (['logout', 'logout-error', 'signed-out'].includes(phaseRef.current)) return;
    const generation = ++epoch.current;
    enter('checking');
    try {
      const me = await api.refreshSession();
      if (mounted.current && generation === epoch.current) accept(me);
    } catch (error) {
      if (mounted.current && generation === epoch.current)
        enter(
          error instanceof AdminApiError && error.kind === 'unauthorized'
            ? currentRef.current
              ? 'expired'
              : 'signed-out'
            : error instanceof AdminApiError && error.kind === 'forbidden'
              ? 'forbidden'
              : 'unavailable',
        );
    }
  }, [api, enter, accept]);
  const logout = useCallback(async () => {
    if (logoutBusy.current) return;
    logoutBusy.current = true;
    ++epoch.current;
    enter('logout');
    try {
      try {
        await api.logout();
      } catch (error) {
        if (!(error instanceof AdminApiError && error.kind === 'unauthorized')) throw error;
      }
      if (!mounted.current) return;
      api.clearSession();
      queryClient.clear();
      currentRef.current = null;
      setCurrent(null);
      enter('signed-out');
    } catch {
      if (mounted.current) enter('logout-error');
    } finally {
      logoutBusy.current = false;
    }
  }, [api, queryClient, enter]);
  useEffect(() => {
    mounted.current = true;
    void check();
    const unsubscribe = api.subscribeToSessionExpiry(() => {
      if (['logout', 'logout-error', 'signed-out'].includes(phaseRef.current)) return;
      ++epoch.current;
      enter(currentRef.current ? 'expired' : 'signed-out');
    });
    let hiddenAt = 0;
    const background = () => {
      hiddenAt = Date.now();
    };
    const foreground = () => {
      if (hiddenAt && Date.now() - hiddenAt >= 30000) {
        hiddenAt = 0;
        if (phaseRef.current === 'ready') void check();
      }
    };
    const visibility = () => {
      if (document.visibilityState === 'hidden') background();
      else foreground();
    };
    const pageshow = (event: PageTransitionEvent) => {
      if (event.persisted && phaseRef.current === 'ready') void check();
    };
    const online = () => {
      if (phaseRef.current === 'unavailable') void check();
    };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('blur', background);
    window.addEventListener('focus', foreground);
    window.addEventListener('pageshow', pageshow);
    window.addEventListener('online', online);
    return () => {
      mounted.current = false;
      ++epoch.current;
      unsubscribe();
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('blur', background);
      window.removeEventListener('focus', foreground);
      window.removeEventListener('pageshow', pageshow);
      window.removeEventListener('online', online);
      api.setSessionLocked(false);
    };
  }, [api, check, enter]);
  useEffect(() => {
    if (phase !== 'ready') return;
    const frame = requestAnimationFrame(() => {
      const node = returnFocus.current;
      returnFocus.current = null;
      if (node?.isConnected && !node.closest('[inert]') && !node.matches(':disabled'))
        node.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [phase]);
  const locked = phase !== 'ready';
  return (
    <SessionActions.Provider value={{ logout: () => void logout() }}>
      {current ? (
        <div
          key={`${current.user.id}:${current.user.role}`}
          inert={locked}
          aria-hidden={locked || undefined}
          style={locked ? { visibility: 'hidden' } : undefined}
        >
          {children(current)}
        </div>
      ) : null}
      {locked ? (
        <SessionGate
          phase={phase}
          modal={Boolean(current)}
          onRetry={() => void check()}
          onLogout={() => void logout()}
          onLogin={async (email, password) => {
            const generation = ++epoch.current;
            const me = await api.login(email, password);
            if (mounted.current && generation === epoch.current) accept(me);
          }}
        />
      ) : null}
    </SessionActions.Provider>
  );
}
